import { useState } from 'react';
import type { JSX } from 'react';
import type { Palette } from '../../../../_theme/palette.js';
import type { SurveyOption } from '../../types.js';
import { hexWithAlpha } from '../../../../_theme/palette.js';

interface ChoiceRowProps {
  option: SurveyOption;
  selected: boolean;
  onClick: () => void;
  palette: Palette;
  multi: boolean;
}

// One easing curve across the field — text input, choice row, star
// rating all share it so the polish reads as intentional.
const PREMIUM_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

export function ChoiceRow({ option, selected, onClick, palette, multi }: ChoiceRowProps): JSX.Element {
  const [hovered, setHovered] = useState(false);
  // Hover lifts text colour muted → text and primes the indicator with an
  // accent-coloured border. The accent fill (and the bold label) is the
  // earned `selected` state only — keeps the hover light enough that a
  // single committed answer stays the clear visual focal point of the row.
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        textAlign: 'left',
        padding: '10px 0',
        border: 'none',
        background: 'transparent',
        color: selected
          ? palette.primary
          : hovered
            ? palette.text
            : palette.textMuted,
        fontSize: 15,
        cursor: 'pointer',
        width: '100%',
        transition: `color 220ms ${PREMIUM_EASE}`,
      }}
    >
      {multi ? (
        <CheckboxIndicator selected={selected} hovered={hovered} palette={palette} />
      ) : (
        <RadioIndicator selected={selected} hovered={hovered} palette={palette} />
      )}
      <span
        style={{
          flex: 1,
          fontWeight: selected ? 600 : 500,
        }}
      >
        {option.label}
      </span>
    </button>
  );
}

function CheckboxIndicator({
  selected,
  hovered,
  palette,
}: {
  selected: boolean;
  hovered: boolean;
  palette: Palette;
}): JSX.Element {
  // Three tiers: idle (faint muted), hovered (primed accent border at 60%),
  // selected (filled accent). Each transitions through the same easing so
  // the whole row feels coordinated.
  const borderColor = selected
    ? palette.primary
    : hovered
      ? hexWithAlpha(palette.primary, 0.6)
      : hexWithAlpha(palette.textMuted, 0.45);
  return (
    <span
      aria-hidden="true"
      style={{
        width: 18,
        height: 18,
        borderRadius: 4,
        border: `2px solid ${borderColor}`,
        background: selected ? palette.primary : 'transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: `border-color 220ms ${PREMIUM_EASE}, background-color 220ms ${PREMIUM_EASE}`,
      }}
    >
      {selected && (
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      )}
    </span>
  );
}

function RadioIndicator({
  selected,
  hovered,
  palette,
}: {
  selected: boolean;
  hovered: boolean;
  palette: Palette;
}): JSX.Element {
  const borderColor = selected
    ? palette.primary
    : hovered
      ? hexWithAlpha(palette.primary, 0.6)
      : hexWithAlpha(palette.textMuted, 0.45);
  return (
    <span
      aria-hidden="true"
      style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: `2px solid ${borderColor}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: `border-color 220ms ${PREMIUM_EASE}`,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: palette.primary,
          transform: selected ? 'scale(1)' : 'scale(0)',
          transition: `transform 220ms ${PREMIUM_EASE}`,
        }}
      />
    </span>
  );
}
